import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Euro, MoreHorizontal, FileText, Loader2, Edit, Copy, Trash2, Eye, EyeIcon, Plane, X, Mail, StickyNote, MessageSquareText, Pencil } from "lucide-react";
import { cn, formatTravelDates } from "@/components/utils/utils";
import { Itinerary } from "@/hooks/useItineraries";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getAvatarColor, getInitials } from "@/components/utils/avatar-colors";
import { useItineraryPDF } from "@/hooks/useItineraryPDF";
import { toast } from "@/hooks/useToast";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { SendQuoteModal } from "./SendQuoteModal";
import { CreateQuoteModal } from "./CreateQuoteModal";
import { ItineraryStatusBadge } from "./ItineraryStatusBadge";
import { Modal } from "@/components/ui/modal";

import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_KEY, SUPABASE_URL } from "@/components/utils/constants";
import { ItineraryStatus } from "../builder/models/itinerary.model";
import { blob } from "stream/consumers";
import { useLanguage } from "@/contexts/LanguageContext";

interface ItineraryCardProps {
  itinerary: Itinerary;
  className?: string;
  onEdit?: (itineraryId: string) => void;
  onDelete?: (itineraryId: string) => void;
  onDuplicate?: (itineraryId: string) => void;
  onViewTravelRequest?: (travelRequestId: string) => void;
  onViewItinerary?: (itineraryId: string) => void;
  onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
  travelRequests?: TravelRequest[];
  isAdmin?: boolean;
}

export const ItineraryCard = ({
  itinerary,
  className,
  onEdit,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onViewItinerary,
  onUpdateStatus,
  isAdmin
}: ItineraryCardProps) => {
  const { t } = useLanguage();
  const { generatePDF } = useItineraryPDF();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Log informazioni dell'itinerario quando il componente viene caricato
  console.log('[ItineraryCard] Loaded itinerary:', itinerary);

  const [isSendQuoteModalOpen, setIsSendQuoteModalOpen] = useState(false);
  const [isCreateQuoteModalOpen, setIsCreateQuoteModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  const handleCreateTripQuote = () => {
    setIsCreateQuoteModalOpen(true);
  };

  const handleViewNotes = () => {
    setIsNotesModalOpen(true);
  };

  // Handler per aprire la nuova modale
  const handleSendQuoteModal = () => {
    setIsSendQuoteModalOpen(true);
  };

  const handleViewTravelRequest = () => {
    onViewTravelRequest(itinerary.travel_request_id);
  };

  const handleViewItinerary = () => {
    if (!onViewItinerary) return;
    onViewItinerary(itinerary.id);
  };

  const handleEdit = () => {
    if (!onEdit) return;
    onEdit(itinerary.id);
  };

  const handleDuplicate = () => {
    if (!onDuplicate) return;
    onDuplicate(itinerary.id);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete(itinerary.id);
  };

  const handleMoveToDraft = async () => {
    if (onUpdateStatus) await onUpdateStatus(itinerary.id, 'draft');
  };

  const handleSendQuote = async (stripeUrl: string, payment: string) => {
    // Chiama la edge function send-quote su Supabase
    try {
      // Genera il PDF e ottieni il base64
      setIsGeneratingPDF(true);
      const pdfResult = await generatePDF(itinerary.id, false);
      const pdfBase64 = pdfResult.pdf.output('datauristring').split(',')[1];
      // pdfResult: { base64: string, filename: string }
      if (!pdfBase64 || !pdfResult?.fileName) {
        toast({
          title: t('errors.generic'),
          description: t('itinerary.pdfConversionError'),
          variant: "destructive",
        });
        setIsGeneratingPDF(false);
        return false;
      }

      // Prepara i dati per la chiamata
      const draftId = itinerary.id;
      const pdfFilename = pdfResult.fileName;
      const to = itinerary.contact_email;
      const subject = `Your Travel Quote Is Ready`;


      // Chiamata alla edge function
      const supabase = createClient(SUPABASE_URL, SUPABASE_KEY)
      const { data, error } = await supabase.functions.invoke("send-quote", {
        body: JSON.stringify({
          draftId,
          pdfBase64,
          pdfFilename,
          to,
          subject,
          variables: {
            client_first_name: itinerary?.contact_first_name,
            client_last_name: itinerary?.contact_last_name,
            itinerary_title: itinerary.title,
            start_date: itinerary.start_date,
            end_date: itinerary.end_date,
            payment,
            stripeUrl
          },
        }),
      });

      setIsGeneratingPDF(false);
      if (error) {
        toast({
          title: t('itinerary.sendQuoteError'),
          description: t('itinerary.sendQuoteErrorDesc'),
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: t('itinerary.quoteSent'),
        description: t('itinerary.quoteSentDesc'),
      });

      // Aggiorna lo stato a sended dopo l'invio
      if (onUpdateStatus) {
        await onUpdateStatus(itinerary.id, 'sended');
      }

      return true;
    } catch (error) {
      setIsGeneratingPDF(false);
      console.error('Errore invio preventivo:', error);
      toast({
        title: t('errors.generic'),
        description: t('itinerary.sendQuoteErrorGeneric'),
        variant: "destructive",
      });
      return false;
    }
  };

  const handleSendItineraryInReview = async (): Promise<boolean> => {
    try {
      if (onUpdateStatus) {
        await onUpdateStatus(itinerary.id, 'pending');
        toast({
          title: t('itinerary.requestSent'),
          description: t('itinerary.requestSentDesc'),
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Errore invio richiesta:', error);
      toast({
        title: t('errors.generic'),
        description: t('itinerary.sendRequestError'),
        variant: "destructive",
      });
      return false;
    }
  };

  const handleGeneratePDF = async () => {
    if (isGeneratingPDF) return; // Prevent multiple clicks

    setIsGeneratingPDF(true);
    try {
      await generatePDF(itinerary.id, true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t('errors.generic'),
        description: t('itinerary.pdfGenerationError'),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Get guest count
  const guestCount = Array.isArray(itinerary.guests_data) ? itinerary.guests_data.length : 1;

  return (
    <Card className={cn("border border-gray-300 rounded-2xl overflow-hidden", className)}>
      <CardContent className="p-0 h-full flex flex-col">
        {/* Image Header */}
        <div className="relative w-full h-[150px]">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-gray-200 bg-cover bg-center"
            style={{
              backgroundImage: itinerary.image
                ? `url(${itinerary.image})`
                : undefined
            }}
          />

          {/* Overlay per pending */}
          {(itinerary.status === 'pending' || itinerary.status === 'expired' || itinerary.status === 'rejected') && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px]" />
          )}

          {/* Badge e messaggi per status */}
          <div>
            <div className="absolute top-3 left-3 flex flex-col gap-2">

              <ItineraryStatusBadge status={itinerary.status} hideDraftQuote={true} />
            </div>
            <div className="absolute bottom-3 left-3 flex flex-col gap-2">

              {/* Bottone review solo per pending e admin */}
              {itinerary.status === 'pending' && isAdmin && (
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-500 bg-gray-100 hover:bg-gray-200"
                    onClick={handleEdit}
                  >
                    {t('itinerary.takeReview')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2">

            {itinerary.note && (
              <Button
                variant="ghost"
                size="sm"
                className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
                title="View Notes"
                onClick={handleViewNotes}
              >
                <MessageSquareText className="h-4 w-4 text-gray-800" />
              </Button>
            )}

            {/* Menu Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
                  title="Menu"
                >
                  <MoreHorizontal className="h-5 w-5 text-gray-800" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleViewItinerary}>
                  <Pencil className="h-4 w-4 mr-2" />
                  { itinerary.status === 'draft' || itinerary.status === 'pending' ? t('itinerary.editItinerary') : t('itinerary.viewItinerary') }
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleViewTravelRequest}>
                  <Eye className="h-4 w-4 mr-2" />
                  {t('travelRequest.viewTripRequest')}
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => window.open(`https://www.backoffice.place2be.it/itineraries/edit/${itinerary.id}`, '_blank')}>
                    <Edit className="h-4 w-4 mr-2" />
                    {t('itinerary.openInBackoffice')}
                  </DropdownMenuItem>
                )}
                {itinerary.status === 'draft' && (
                  <>
                    <DropdownMenuItem onClick={handleCreateTripQuote}>
                      <FileText className="h-4 w-4 mr-2" />
                      {t('itinerary.submitForApproval')}
                    </DropdownMenuItem>

                  </>
                )}

                <DropdownMenuSeparator />

                {itinerary.status === 'approved' && (
                  <>
                    <DropdownMenuItem onClick={handleSendQuoteModal}>
                      <Mail className="h-4 w-4 mr-2" />
                      {t('itinerary.sendToClient')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {/* {(itinerary.status === 'rejected') && (
                  <>
                    <DropdownMenuItem onClick={handleMoveToDraft}>
                      <Mail className="h-4 w-4 mr-2" />
                      Move to draft
                    </DropdownMenuItem>
                  </>
                )} */}

                <DropdownMenuItem onClick={handleGeneratePDF} disabled={isGeneratingPDF}>
                  {isGeneratingPDF ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  {isGeneratingPDF ? t('itinerary.generatingPDF') : t('itinerary.generatePDF')}
                </DropdownMenuItem>


                {(itinerary.status !== 'sended' && itinerary.status !== 'completed') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleDuplicate}>
                      <Copy className="h-4 w-4 mr-2" />
                      {t('common.duplicate')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </>
                )}

              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col">
          {/* Title */}
          <h3 className="text-lg font-semibold text-black mb-4 line-clamp-1">
            {itinerary.title || t('itinerary.untitledItinerary')}
          </h3>

          {/* Trip Info Row */}
          <div className="flex items-center justify-between mb-4">
            {/* Dates */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-xs text-gray-600">
                {formatTravelDates(itinerary.start_date, itinerary.end_date)}
              </span>
            </div>

            {/* People */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-600">
                {guestCount}
              </span>
            </div>

            {/* Total */}
            <div className="flex items-center gap-2">
              <Euro className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-600">
                {itinerary.total}
              </span>
            </div>
          </div>

          {/* User Info */}
          {itinerary && (
            <div className="flex items-center justify-between gap-2">
              {/* Contact info */}
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className={`text-xs text-white ${getAvatarColor(itinerary.contact_email)}`}>
                    {getInitials(itinerary.contact_first_name, itinerary.contact_last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-gray-900 font-medium">
                  {itinerary.contact_first_name} {itinerary.contact_last_name}
                </span>
              </div>

              <ItineraryStatusBadge
                status={itinerary.status === 'draft' ? 'draft' : 'quote'}
              />
            </div>
          )}
        </div>
      </CardContent>

      {/* Modale per invio preventivo (SendQuoteModal, ex TripQuoteModal) */}
      <SendQuoteModal
        isOpen={isSendQuoteModalOpen}
        onClose={() => setIsSendQuoteModalOpen(false)}
        itinerary={itinerary}
        onSendQuote={handleSendQuote}
      />

      <CreateQuoteModal
        isOpen={isCreateQuoteModalOpen}
        onClose={() => setIsCreateQuoteModalOpen(false)}
        onSend={async () => { await handleSendItineraryInReview(); }}
      />

      {/* Modale per visualizzare le note */}
      <Modal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        title={t('itinerary.itineraryNotes')}
        size="sm"
      >
        <div className="p-6">
          {itinerary.note ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                {itinerary.note}
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <StickyNote className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('itinerary.noNotesAvailable')}</p>
            </div>
          )}
        </div>
      </Modal>

    </Card>
  );
};
